import { Checkbox, Loader, Select, SegmentedControl } from '@mantine/core';
import { joinNames } from '@reditelji/shared';
import { useEffect, useRef, useState } from 'react';
import type { MailPreview } from '../api/mail';

/**
 * The rendered mail, in an iframe.
 *
 * Mail HTML carries its own inline styles and its own background, and it is not
 * written against this app's stylesheet — an iframe is what keeps the two from
 * borrowing each other's rules, and it is closer to what a mail client does
 * anyway.
 */
export function MailPreviewPane({
  preview,
  pending,
  volunteerId,
  onVolunteer,
  volunteers,
  household,
  onHousehold,
  hint,
}: {
  preview: MailPreview | null;
  pending: boolean;
  volunteerId: string | null;
  onVolunteer: (id: string | null) => void;
  volunteers: { value: string; label: string }[];
  /** With nobody picked: render the sample as a mail to two people on one address. */
  household: boolean;
  onHousehold: (on: boolean) => void;
  /**
   * What the rendered mail is, in words, when the default — written for a
   * template, with its buttons and shifts — would not be true of this mail.
   */
  hint?: string;
}) {
  const [view, setView] = useState<'html' | 'text'>('html');
  // A picked volunteer who shares their inbox is previewed as the one mail
  // the household gets; the server says whom it rendered for.
  const people = volunteerId && preview && preview.people.length > 1 ? preview.people : null;

  return (
    <aside className="ml-preview">
      <div className="ml-preview-head">
        <div className="ml-eyebrow">PREDOGLED</div>
        <Select
          size="xs"
          searchable
          clearable
          placeholder="vzorčni podatki"
          nothingFoundMessage="Ni zadetkov"
          data={volunteers}
          value={volunteerId}
          onChange={onVolunteer}
          comboboxProps={{ withinPortal: true }}
        />
        <div className="ml-hint">
          {hint ??
            (people
              ? `Skupni predal — ${joinNames(people, 'sl')}: eno sporočilo, gumb za vsakega. Nič se ne pošlje.`
              : volunteerId
                ? 'Prava imena, izmene in spremembe te osebe. Nič se ne pošlje.'
                : 'Vzorčni podatki. Izberi prostovoljca za pravo vsebino.')}
        </div>
        {volunteerId === null ? (
          <Checkbox
            size="xs"
            mt={6}
            label="Vzorec za skupni predal — dve osebi na enem naslovu"
            checked={household}
            onChange={(e) => onHousehold(e.currentTarget.checked)}
          />
        ) : null}
      </div>

      {preview === null ? (
        <div className="ml-preview-empty">{pending ? <Loader size="sm" /> : 'Ni predogleda.'}</div>
      ) : (
        <>
          <div className="ml-preview-subject">
            <span className="ml-preview-label">Zadeva</span>
            <span className="ml-preview-value">{preview.subject || '— brez zadeve —'}</span>
          </div>

          <SegmentedControl
            size="xs"
            fullWidth
            value={view}
            onChange={(v) => setView(v)}
            data={[
              { value: 'html', label: 'Oblikovano' },
              { value: 'text', label: 'Golo besedilo' },
            ]}
          />

          <div className={`ml-preview-body${pending ? ' ml-preview-body--stale' : ''}`}>
            {view === 'html' ? (
              <PreviewFrame html={preview.html} />
            ) : (
              <pre className="ml-preview-text">{preview.text}</pre>
            )}
          </div>
        </>
      )}
    </aside>
  );
}

function PreviewFrame({ html }: { html: string }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const watching = useRef<ResizeObserver | null>(null);
  const [height, setHeight] = useState(360);

  const measure = () => {
    const body = frame.current?.contentDocument?.body;
    if (body) setHeight(Math.max(280, body.scrollHeight));
  };

  // A new body is a new document: stop watching the one being replaced, and
  // collapse until the load event can measure what arrives.
  useEffect(() => {
    watching.current?.disconnect();
    setHeight(360);
  }, [html]);

  useEffect(() => () => watching.current?.disconnect(), []);

  /*
   * The frame is sized to its content, so it never scrolls itself — which makes
   * a height measured once and left alone a silent crop the moment the mail is
   * taller than it says. The logo is a remote image with an auto height, so the
   * body can still grow after the load event that would have measured it; and
   * the shell sits below its 560px cap at this pane width, so it would reflow
   * again if the pane ever changed width. Watching the body covers both.
   * Nothing in the mail is sized to the viewport, so `scrollHeight` does not
   * follow the frame's own height and setting one cannot feed the other.
   */
  const watch = () => {
    const body = frame.current?.contentDocument?.body;
    watching.current?.disconnect();
    if (!body) return;
    measure();
    watching.current = new ResizeObserver(measure);
    watching.current.observe(body);
  };

  return (
    <iframe
      ref={frame}
      className="ml-preview-frame"
      title="Predogled sporočila"
      // No allow-scripts: nothing in a mail body needs to run, and the
      // volunteer's client would not run it either. allow-same-origin only so
      // the frame can be measured to its content — with scripts denied there
      // is nothing inside to make use of it.
      sandbox="allow-same-origin"
      style={{ height }}
      srcDoc={`<!doctype html><meta charset="utf-8"><style>body{margin:0}</style>${html}`}
      onLoad={watch}
    />
  );
}
